import * as React from "react";
import ReactSelect from "react-select";
import { Theme } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import theme from "styles/theme";
import { Placeholder } from "./placeholder";
import { Control } from "./control";
import { KEYBOARD_KEYS } from "constants/keyboard";

interface IDropdownProps {
    onChange: (selectedOption: IOption) => void;
    options: IOption[];
    placeholder: string;
    defaultValue: IOption;
}

interface IOption {
    label: string;
}

const useStyles = makeStyles<Theme>({
    root: {
        flexGrow: 1,
        margin: "0 10px",
        zIndex: 10,
    },
    text: {
        color: theme.palette.grey,
        "& .MuiInput-underline:hover:before": {
            borderBottomWidth: 1,
        },
        "& .MuiInput-underline:before": {
            borderBottomColor: theme.palette.grey,
        },
        "& .MuiInput-underline:after": {
            borderBottomColor: theme.palette.darkestGrey,
            borderBottomWidth: 1,
        },
    },
    input: {
        display: "flex",
        padding: 0,
        height: 32,
        fontSize: 13,
        cursor: "pointer",
    },
    flexContainer: {
        display: "flex",
        flexWrap: "nowrap",
        flex: 1,
        alignItems: "center",
        overflow: "hidden",
    },
    placeholder: {
        fontSize: 14,
        color: "#9197a3",
    },
});

const components = {
    Control,
    Placeholder,
};

export const Dropdown: React.FC<IDropdownProps> = ({ onChange, options, placeholder, defaultValue }) => {
    const classes = useStyles({});
    const reactSelectRef = React.useRef<ReactSelect>();
    const divRef = React.useRef<HTMLDivElement>();
    const handleStopPropagationOnEnter = React.useCallback((e: KeyboardEvent) => {
        if (e.key === KEYBOARD_KEYS.ENTER) {
            const lastFocusedOption = reactSelectRef.current.select.state.focusedOption;
            if (lastFocusedOption) {
                e.stopPropagation();
                reactSelectRef.current.select.selectOption(lastFocusedOption);
            }
        }
    }, []);

    React.useEffect(() => {
        divRef.current.addEventListener("keydown", handleStopPropagationOnEnter);
        return () => {
            divRef.current.removeEventListener("keydown", handleStopPropagationOnEnter); // eslint-disable-line react-hooks/exhaustive-deps
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div ref={divRef}>
            <ReactSelect
                className={classes.root}
                classes={classes}
                defaultValue={defaultValue}
                ref={reactSelectRef}
                components={components}
                onChange={onChange}
                options={options}
                placeholder={placeholder}
                isClearable={false}
                isSearchable={false}
                maxMenuHeight={180}
            />
        </div>
    );
};
